/**
 * SP Design Your Ring - Cart Edit Handler
 * 
 * Handles the "Edit" button in the cart drawer for DYR items.
 * Restores the state from line item properties and redirects to step 3.
 * 
 * This file is separate from sp-dyr.js because the edit button can be clicked
 * from anywhere on the site, not just the DYR landing page.
 */

(function() {
  'use strict';

  // Fallback landing page for cart items added before we started tracking
  // the originating DYR page (_DYR-Page line item property).
  const DYR_LANDING_FALLBACK_URL = '/pages/design-your-ring';

  /**
   * Initialize edit button listeners
   */
  function init() {
    console.log('🎨 SP DYR Cart Edit initialized');
    
    // Listen for clicks on edit buttons (using event delegation)
    document.addEventListener('click', handleEditClick);
    
    // Also listen for cart drawer updates (when cart is refreshed)
    observeCartDrawer();
  }

  /**
   * Handle click on edit button
   */
  function handleEditClick(e) {
    const editButton = e.target.closest('[data-diamond-edit]');
    
    if (!editButton) return;
    
    e.preventDefault();
    
    console.log('✏️ Edit DYR item clicked');
    
    // Get all data from button attributes
    const data = {
      ring: {
        variantId: editButton.dataset.ringVariantId,
        handle: editButton.dataset.ringHandle,
        title: editButton.dataset.ringTitle,
        image: editButton.dataset.ringImage,
      },
      ringSize: editButton.dataset.ringSize,
      karat: editButton.dataset.ringKarat,
      diamond: {
        shape: editButton.dataset.diamondShape,
        carat: editButton.dataset.diamondCarat,
        cut: editButton.dataset.diamondCut,
        polish: editButton.dataset.diamondPolish,
        symmetry: editButton.dataset.diamondSymmetry,
        clarity: editButton.dataset.diamondClarity,
        color: editButton.dataset.diamondColor,
        image: editButton.dataset.diamondImage,
        title: editButton.dataset.diamondTitle,
        price: editButton.dataset.diamondPrice,
      },
      previewKey: editButton.dataset.previewKey || null,
      dyrPage: editButton.dataset.dyrPage || null,
      lineKey: editButton.dataset.lineKey
    };
    
    console.log('📦 Line item data:', data);
    
    // Fetch full product data and build state
    buildStateAndRedirect(data);
  }

  /**
   * Fetch full product data and build state for localStorage
  */

  async function buildStateAndRedirect(data) {
    try {
      console.log('📡 Fetching ring product data...');
      
      // Fetch the ring product JSON
      const response = await fetch(`/products/${data.ring.handle}.js`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch product data');
      }
      
      const ringProduct = await response.json();
      
      console.log('✅ Ring product fetched:', ringProduct);
      
      // Find the specific variant
      const selectedVariant = ringProduct.variants.find(v => v.id === parseInt(data.ring.variantId));
      
      if (!selectedVariant) {
        throw new Error('Variant not found');
      }
      
      // Build the state object to save in localStorage
      const state = {
        currentStep: 3, // We'll redirect to step 3 (review)
        selectedRing: {
          id: ringProduct.id,
          title: ringProduct.title,
          handle: ringProduct.handle,
          variants: ringProduct.variants,
          images: ringProduct.images,
          featured_image: ringProduct.featured_image,
          options: ringProduct.options,
          selectedVariant: selectedVariant,
          selectedKarat: data.karat || null,
          price: ringProduct.price,
          tags: ringProduct.tags
        },
        selectedDiamond: {
          // We'll need to reconstruct diamond data from properties
          shape: extractCode(data.diamond.shape),
          shapeLabel: data.diamond.shape,
          carat: parseFloat(data.diamond.carat),
          cut: extractCode(data.diamond.cut),
          cutLabel: data.diamond.cut,
          // Store labels as-is (not run through extractCode) — Polish/Symmetry
          // labels ("Excellent", "Very Good") don't need code extraction, and
          // getDiamondGradeSpecs falls back to matching by label anyway.
          polish: data.diamond.polish || '',
          polishLabel: data.diamond.polish || '',
          symmetry: data.diamond.symmetry || '',
          symmetryLabel: data.diamond.symmetry || '',
          clarity: extractCode(data.diamond.clarity),
          clarityLabel: data.diamond.clarity,
          color: extractCode(data.diamond.color),
          colorLabel: data.diamond.color,
          image: data.diamond.image,
          title: data.diamond.title,
          price: parseInt(data.diamond.price, 10) / 100,
          priceCents: parseInt(data.diamond.price, 10),
          priceFormatted: formatMoney(parseInt(data.diamond.price, 10))
        },
        selectedRingSize: data.ringSize
      };
      
      console.log('💾 Saving state to localStorage:', state);
      
      // Save to localStorage
      localStorage.setItem('sp-dyr-state', JSON.stringify(state));
      
      console.log('✅ State saved successfully');
      
      // Redirect back to the DYR page this item was built on (falls back for
      // items added before we tracked the originating page)
      const landingUrl = data.dyrPage || DYR_LANDING_FALLBACK_URL;

      const redirectParams = new URLSearchParams({ step: '3' });
      if (data.previewKey) {
        redirectParams.set('preview_key', data.previewKey);
        console.log('🔑 Including preview_key in redirect:', data.previewKey);
      }
      const redirectUrl = `${landingUrl}?${redirectParams.toString()}`;
      
      // Redirect to step 3
      window.location.href = redirectUrl;
      
    } catch (error) {
      console.error('❌ Error building state:', error);
      alert('There was an error loading your design. Please try again.');
    }
  }

  /**
   * Extract code from label (e.g., "Round Brilliant" -> "Round")
   * This is a simplified version - adjust as needed based on your data
   */

  function extractCode(label) {
    if (!label) return '';
    
    // Remove common suffixes
    return label
      .replace(' Brilliant', '')
      .replace(' Cut', '')
      .trim()
      .toLowerCase();
  }

  /**
   * Format money (basic version - adjust to match your theme's format)
   */

  function formatMoney(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }

  /**
   * Observe cart drawer for dynamic updates
   * When cart is refreshed, new edit buttons are added
   */
  
  function observeCartDrawer() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Check if cart drawer was updated
        if (mutation.target.matches('cart-drawer') || 
            mutation.target.closest('cart-drawer')) {
          console.log('🔄 Cart drawer updated, edit buttons ready');
        }
      });
    });
    
    // Start observing the document body for cart drawer changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

